import React, { useState, useEffect } from 'react'
import { showSuccess, showError } from '../utils/notifications.js'

export default function ImportFundAssetsModal({ 
  fundId, 
  isOpen, 
  onClose, 
  onImportComplete,
  bulkCreateUrl,
  searchUrl,
  csrfToken,
  initialText = ''
}) {
  const [rawInput, setRawInput] = useState('')
  const [parsedAssets, setParsedAssets] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  
  // Auto-parse when modal opens with initialText
  useEffect(() => {
    if (isOpen && initialText) {
      setRawInput(initialText)
      // Auto-parse immediately
      handleParse(initialText)
    }
  }, [isOpen, initialText])

  const handleInputChange = (e) => {
    const text = e.target.value
    setRawInput(text)
  }

  const handleParse = async (textToParse = null) => {
    const text = textToParse || rawInput
    setIsProcessing(true)
    
    // Split by newline, trim, filter empty
    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
    
    if (lines.length === 0) {
      showError('No valid lines found')
      setIsProcessing(false)
      return
    }
    
    // Check which assets already exist
    try {
      const response = await fetch(`${searchUrl}?fund_id=${fundId}&q=`)
      const existingAssets = await response.json()
      
      const existingTitles = new Set(existingAssets.map(a => a.title.toLowerCase()))
      
      const assets = lines.map(title => ({
        title,
        status: existingTitles.has(title.toLowerCase()) ? 'found' : 'new'
      }))
      
      setParsedAssets(assets)
    } catch (error) {
      showError('Failed to check existing assets')
      console.error(error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleImport = async () => {
    setIsImporting(true)
    
    try {
      const titles = parsedAssets.map(a => a.title)
      
      const response = await fetch(bulkCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          bulk_create: { titles },
          fund_id: fundId
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        const createdCount = data.created.length
        const existingCount = data.existing.length
        showSuccess(`Import complete: ${createdCount} created, ${existingCount} already existed`)
        onImportComplete(data.created, data.existing)
        handleClose()
      } else {
        showError('Import failed')
      }
    } catch (error) {
      showError('Failed to import assets')
      console.error(error)
    } finally {
      setIsImporting(false)
    }
  }

  const handleClose = () => {
    setRawInput('')
    setParsedAssets([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Import Fund Assets</h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>
          
          <div className="modal-body">
            {parsedAssets.length === 0 ? (
              <div>
                <label className="form-label">Paste asset names (one per line):</label>
                <textarea
                  className="form-control"
                  rows="10"
                  value={rawInput}
                  onChange={handleInputChange}
                  placeholder="..."
                  disabled={isProcessing}
                />
                <div className="form-text">
                  Copy and paste from spreadsheet (one asset per line)
                </div>
              </div>
            ) : (
              <div>
                <h6>Preview ({parsedAssets.length} assets)</h6>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-sm">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Asset Name</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedAssets.map((asset, index) => (
                        <tr key={index}>
                          <td>{asset.title}</td>
                          <td>
                            <span className={`badge ${asset.status === 'new' ? 'bg-success' : 'bg-secondary'}`}>
                              {asset.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleClose}
              disabled={isProcessing || isImporting}
            >
              Cancel
            </button>
            
            {parsedAssets.length === 0 ? (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleParse}
                disabled={!rawInput.trim() || isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Parse'}
              </button>
            ) : (
              <>
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={() => setParsedAssets([])}
                  disabled={isImporting}
                >
                  Back
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleImport}
                  disabled={isImporting}
                >
                  {isImporting ? 'Importing...' : `Import ${parsedAssets.length} Assets`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

